import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { styled as muiStyled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert,
  InputLabel,
  Container,
} from '@mui/material';

const FormContainer = muiStyled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  maxWidth: '650px',
  margin: 'auto',
  marginTop: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down('md')]: {
    maxWidth: '320px',
    padding: theme.spacing(2),
  }
}));

const StyledForm = muiStyled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
}));

const StyledTextField = muiStyled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const StyledFormControl = muiStyled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
}));

const StyledButton = muiStyled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  boxShadow: theme.shadows[3],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const StyledLink = muiStyled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 600,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// Storage keys for persisting registration state
const STORAGE_KEYS = {
  REGISTRATION_DATA: 'registration_form_data',
  REGISTRATION_STATE: 'registration_state',
  REGISTRATION_ERROR: 'registration_error',
  REGISTRATION_ACTIVE: 'registration_flow_active'
};

const Register = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const registrationActiveRef = useRef(false);
const [usernameError, setUsernameError] = useState(false);
  const [user, setUser] = useState({
    username: '',
    email: '',
    code: '',
    password: '',
    usertype: '',
    authCode: '',
    confirmpassword: '',
  });


  // Validate username for spaces and special characters
const validateUsername = (username) => {
  // Check if username contains spaces
  if (username.includes(' ')) {
    return false;
  }
  // You can add more validation rules here if needed
  // For example: only allow alphanumeric and underscores
  // const usernameRegex = /^[a-zA-Z0-9_]+$/;
  // return usernameRegex.test(username);
  
  return true;
};
// Get username helper text based on validation
const getUsernameHelperText = () => {
  if (!user.username) {
    return 'Username cannot contain spaces or special characters';
  }
  if (usernameError) {
    return 'Username cannot contain spaces. Please remove spaces.';
  }
  return 'Username looks good!';
};
  // Mark registration flow as active
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_ACTIVE, 'true');
    registrationActiveRef.current = true;
    
    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      // Only clear if registration was completed successfully
      if (!confirm && !user.username) {
        sessionStorage.removeItem(STORAGE_KEYS.REGISTRATION_ACTIVE);
      }
    };
  }, []);

  // Prevent navigation away from registration if in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (registrationActiveRef.current && (user.username || user.email || confirm)) {
        e.preventDefault();
        e.returnValue = 'You have unsaved registration data. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (registrationActiveRef.current && (user.username || user.email || confirm)) {
        const shouldLeave = window.confirm('You have unsaved registration data. Are you sure you want to leave?');
        if (!shouldLeave) {
          e.preventDefault();
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user, confirm]);

  // Load persisted state on component mount
  useEffect(() => {
    try {
      const savedFormData = sessionStorage.getItem(STORAGE_KEYS.REGISTRATION_DATA);
      const savedState = sessionStorage.getItem(STORAGE_KEYS.REGISTRATION_STATE);
      const savedError = sessionStorage.getItem(STORAGE_KEYS.REGISTRATION_ERROR);
      const isRegistrationActive = sessionStorage.getItem(STORAGE_KEYS.REGISTRATION_ACTIVE);

      // If registration was active, restore the state
      if (isRegistrationActive === 'true') {
        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          setUser(parsedData);
        }

        if (savedState) {
          const parsedState = JSON.parse(savedState);
          setConfirm(parsedState.confirm || false);
        }

        if (savedError) {
          setError(savedError);
        }

        // Show a message that data was restored
        if (savedFormData || savedState) {
          setTimeout(() => {
            if (mountedRef.current) {
              setError('Your registration data has been restored after page refresh.');
            }
          }, 100);
        }
      }
    } catch (err) {
      console.error('Error loading persisted registration state:', err);
      // Clear corrupted data
      clearPersistedData();
    }
  }, []);

  // Persist form data whenever user state changes
  useEffect(() => {
    if (user.username || user.email || user.code || user.usertype) {
      sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_DATA, JSON.stringify(user));
      sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_ACTIVE, 'true');
    }
  }, [user]);

  // Persist confirmation state
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_STATE, JSON.stringify({ confirm }));
    if (confirm) {
      sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_ACTIVE, 'true');
    }
  }, [confirm]);

  // Persist error state
  useEffect(() => {
    if (error) {
      sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_ERROR, error);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.REGISTRATION_ERROR);
    }
  }, [error]);

  // Clear all persisted registration data
  const clearPersistedData = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });
    registrationActiveRef.current = false;
  };

  // Update the handleInputChange function to handle both Select and TextField changes
  const handleInputChange = (e) => {
    // For Select, name is in target.name and for TextField it's in target.id
    const fieldName = e.target.name || e.target.id;
    const value = e.target.value;
      if (fieldName === 'username') {
    const isValid = validateUsername(value);
    setUsernameError(!isValid && value.length > 0);
  }
    setUser(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!user.usertype) {
      setError('Please select a user type');
      return false;
    }
    if (!user.username) {
      setError('Please enter a username');
      return false;
    }
    if (!validateUsername(user.username)) {
  setError('Username cannot contain spaces or special characters');
  setUsernameError(true);
  return false;
}
    if (!user.code) {
      setError('Please enter a talent code');
      return false;
    }
    if (!user.email) {
      setError('Please enter an email');
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!user.email || !emailRegex.test(user.email.toLowerCase())) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!user.password) {
      setError('Please enter a password');
      return false;
    }
    if (!user.confirmpassword) {
      setError('Please confirm your password');
      return false;
    }
    if (user.password !== user.confirmpassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
  event.preventDefault();
  if (!validateForm()) return;
  
  setLoading(true);
  try {
    const usertype = {
      candidate: 1,
      podprovider: 2,
      manager: 0,
      stakeholder: 4,
      partnerguest: 5,
      customer: 3
    }[user.usertype];

    const registrationResponse = await axios.post(
      process.env.REACT_APP_CANDIDATESIGNUP,
      {
        usertype,
        email: user.email,
        code: user.code,
      }
    );

    // Check the actual response structure
    if (registrationResponse.data.status === 200) {
      const signUpResponse = await Auth.signUp({
        username: user.username,
        password: user.password,
        attributes: {
          email: user.email,
        },
      });
      setConfirm(true);
      setError(null); // Clear any previous errors
    } else if (registrationResponse.data.status === 400) {
      // Use the actual error message from the API response
      setError(registrationResponse.data.data || 'Invalid invite code');
       alert(registrationResponse.data.data || 'Invalid invite code');
    } else {
      // Handle other status codes
      setError(registrationResponse.data.data || 'Registration failed. Please try again.');
    }
  } catch (err) {
    // Handle network errors or other exceptions
    console.error('Registration error:', err);
    
    // Check if it's an API error response
    if (err.response && err.response.data) {
      if (err.response.data.status === 400) {
        setError(err.response.data.data || 'Invalid invite code');
      } else {
        setError(err.response.data.data || err.response.data.message || 'Registration failed. Please try again.');
      }
    } else {
      // Network error or other issue
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
    }
  } finally {
    setLoading(false);
  }
};

  const handleConfirmSignUp = async (event) => {
    event.preventDefault();
    if (!user.authCode) {
      setError('Please enter the confirmation code');
      return;
    }

    setLoading(true);
    try {
      const confirmResult = await Auth.confirmSignUp(user.username, user.authCode);
      if (confirmResult === 'SUCCESS') {
        // Mark registration as completed successfully
        registrationActiveRef.current = false;
        
        // Clear persisted data on successful registration
        clearPersistedData();
        
        // Add a success message before redirecting
        alert('Registration completed successfully! Redirecting to login...');
        
        // Small delay to ensure user sees the success message
        setTimeout(() => {
          if (mountedRef.current) {
            history.push('/login');
          }
        }, 1000);
      }
    } catch (err) {
      const errorMessage = err.message || 'Verification failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Clear form data and start fresh
  const handleStartOver = () => {
    if (window.confirm('Are you sure you want to clear all registration data and start over?')) {
      clearPersistedData();
      setUser({
        username: '',
        email: '',
        code: '',
        password: '',
        usertype: '',
        authCode: '',
        confirmpassword: '',
      });
      setConfirm(false);
      setError(null);
      setUsernameError(false);
      registrationActiveRef.current = false;
    }
  };

  // Handle going back from confirmation to registration form
  const handleBackToRegistration = () => {
    setConfirm(false);
    setError(null);
    // Keep the registration active flag
    sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_ACTIVE, 'true');
  };

  return (
    <Container maxWidth="md">
     

      {!confirm ? (
        <FormContainer elevation={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Register
          </Typography>

          {loading && <CircularProgress sx={{ my: 2 }} />}

          <StyledForm onSubmit={handleSubmit}>
            <StyledFormControl>
              <InputLabel>Select User Type</InputLabel>
              <Select
              required
                value={user.usertype}
                onChange={handleInputChange}
                name="usertype" 
                id="usertype"
                label="Select User Type"
              >
                <MenuItem value="candidate">Talent</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="stakeholder">Stake Holder</MenuItem>
                <MenuItem value="customer">Admin</MenuItem>
              </Select>
            </StyledFormControl>

            <StyledTextField
              fullWidth
              required
              id="username"
              label="Username"
              value={user.username}
              onChange={handleInputChange}
              variant="outlined"
                error={usernameError}
               helperText={getUsernameHelperText()}
  FormHelperTextProps={{
    style: {
      color: usernameError ? '#d32f2f' : (!user.username ? '#666' : '#4caf50')
    }
  }}
            />

            <StyledTextField
              fullWidth
              required
              id="email"
              label="Email"
              type="email"
              value={user.email}
              onChange={handleInputChange}
              variant="outlined"
            />

            <StyledTextField
              fullWidth
              required
              id="password"
              label="Password"
              type="password"
              value={user.password}
              onChange={handleInputChange}
              variant="outlined"
            />

            <StyledTextField
              fullWidth
              required
              id="confirmpassword"
              label="Confirm Password"
              type="password"
              value={user.confirmpassword}
              onChange={handleInputChange}
              variant="outlined"
            />

            <StyledTextField
              fullWidth
              required
              id="code"
              label="Invite Code"
              value={user.code}
              onChange={handleInputChange}
              variant="outlined"
              helperText="Safe to navigate away or refresh - your progress is automatically saved"
            />

            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
               disabled={loading || usernameError}
            >
              {loading ? <CircularProgress size={24} /> : 'Register'}
            </StyledButton>

            <Box mt={2} textAlign="center">
              <Typography variant="body1">
                Already have an account?{' '}
                <StyledLink to="/login">
                  Login
                </StyledLink>
              </Typography>
            </Box>

            {/* Add option to clear form data */}
            <Box mt={1} textAlign="center">
              <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={handleStartOver}
              >
                Clear Form & Start Over
              </Button>
            </Box>
          </StyledForm>
        </FormContainer>
      ) : (
        <FormContainer elevation={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome!
          </Typography>

          <Typography variant="body1" align="center" paragraph>
            You have successfully registered with artemy.ai
          </Typography>

          <Typography variant="body1" align="center" paragraph>
            We've sent you an email. Please enter the code to verify your account
          </Typography>

          <Typography variant="body2" align="center" paragraph color="text.secondary">
            Safe to navigate away or refresh - your verification progress is preserved
          </Typography>

          <StyledForm onSubmit={handleConfirmSignUp}>
            <StyledTextField
              fullWidth
              id="authCode"
              label="Confirmation Code"
              type="text"
              value={user.authCode}
              onChange={handleInputChange}
              variant="outlined"
              placeholder="Enter 6-digit code"
              autoComplete="one-time-code"
            />

            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify Account'}
            </StyledButton>

            <Box mt={2} textAlign="center">
              <Button
                variant="text"
                color="secondary"
                onClick={handleBackToRegistration}
              >
                Back to Registration
              </Button>
            </Box>
          </StyledForm>
        </FormContainer>
      )}
    </Container>
  );
};

export default Register;